import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Switch, Route, useHistory, Redirect } from "react-router-dom";
import { RootState, AppDispatch } from "store";
import { loginSuccess } from "store/slices/authSlice";
import { API_BASE_URL, OAUTH_CONFIG } from "config";

import LoginForm from "components/LoginForm";
import TradingDashboard from "components/TradingDashboard";
import WebSocketManager from "components/WebSocketManager";
import TwitterSuccess from "components/TwitterSuccess";
import Landing from "components/Landing/Landing";
import { ResetPasswordPage } from "features/password-recovery";
import { ConfirmEmailChangePage } from "features/change-email";
import { RAFFLE_ROUTE, RaffleDetailPage } from "widgets/wheel-raffles";

const GoogleCallback: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      const exchangeCodeForToken = async () => {
        try {
          const redirectUri = OAUTH_CONFIG.google.redirectUri;

          const response = await fetch(`${API_BASE_URL}/auth/google/token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              code,
              redirect_uri: redirectUri,
            }),
          });

          if (response.ok) {
            const data = await response.json();

            dispatch(
              loginSuccess({
                user: data.user,
                access_token: data.access_token,
              }),
            );

            history.push("/dashboard");
          } else {
            history.push("/login");
          }
        } catch (error) {
          history.push("/login");
        }
      };

      exchangeCodeForToken();
    } else {
      history.push("/login");
    }
  }, [dispatch, history]);

  return <div>Authenticating with Google...</div>;
};

const RedditCallback: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      const exchangeCodeForToken = async () => {
        try {
          const redirectUri = OAUTH_CONFIG.reddit.redirectUri;

          const response = await fetch(`${API_BASE_URL}/auth/reddit/token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              code,
              redirect_uri: redirectUri,
            }),
          });

          if (response.ok) {
            const tokenData = await response.json();

            if (!tokenData.access_token) {
              throw new Error("Получен некорректный токен от Reddit");
            }

            if (!tokenData.user_info || !tokenData.user_info.id) {
              throw new Error(
                "Не удалось получить информацию о пользователе Reddit",
              );
            }

            const authResponse = await fetch(
              `${API_BASE_URL}/auth/oauth/reddit`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  oauth_token: tokenData.access_token,
                  oauth_id: tokenData.user_info.id,
                }),
              },
            );

            if (!authResponse.ok) {
              throw new Error("Ошибка при авторизации через Reddit");
            }

            const authData = await authResponse.json();

            dispatch(
              loginSuccess({
                user: authData.user,
                access_token: authData.access_token,
              }),
            );

            history.push("/dashboard");
          } else {
            history.push("/login");
          }
        } catch (error) {
          history.push("/login");
        }
      };

      exchangeCodeForToken();
    } else {
      history.push("/login");
    }
  }, [dispatch, history]);

  return <div>Authenticating with Reddit...</div>;
};

const TwitterCallback: React.FC = () => {
  const history = useHistory();

  useEffect(() => {
    history.push("/login/twitter/success");
  }, [history]);

  return <div>Authenticating with Twitter...</div>;
};

const AppRouter: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  return (
    <Switch>
      <Route
        path="/"
        exact
        render={() =>
          isAuthenticated ? <Redirect to="/dashboard" /> : <Landing />
        }
      />
      <Route
        path="/dashboard"
        exact
        render={() =>
          isAuthenticated ? (
            <>
              <WebSocketManager />
              <TradingDashboard />
            </>
          ) : (
            <Redirect to="/" />
          )
        }
      />
      <Route
        path={RAFFLE_ROUTE}
        exact
        render={() =>
          isAuthenticated ? (
            <>
              {/* Сокет нужен и здесь: баланс в шапке обновляется только по нему. */}
              <WebSocketManager />
              <RaffleDetailPage />
            </>
          ) : (
            <Redirect to="/" />
          )
        }
      />
      <Route
        path="/login"
        exact
        render={() =>
          !isAuthenticated ? (
            <LoginForm type="login" />
          ) : (
            <Redirect to="/dashboard" />
          )
        }
      />
      <Route
        path="/register"
        exact
        render={() =>
          !isAuthenticated ? (
            <LoginForm type="register" />
          ) : (
            <Redirect to="/dashboard" />
          )
        }
      />
      <Route
        path="/reset-password"
        exact
        render={() =>
          !isAuthenticated ? (
            <ResetPasswordPage />
          ) : (
            <Redirect to="/dashboard" />
          )
        }
      />
      <Route
        path="/profile/email-confirm"
        exact
        render={() => <ConfirmEmailChangePage />}
      />
      <Route
        path="/login/google/authorized"
        render={() => <GoogleCallback />}
      />
      <Route
        path="/login/reddit/authorized"
        render={() => <RedditCallback />}
      />

      <Route path="/twitter/callback" render={() => <TwitterCallback />} />
      <Route path="/login/twitter/success" render={() => <TwitterSuccess />} />
    </Switch>
  );
};

export default AppRouter;
